import React, { useState, useEffect } from 'react';
import './GameStatsTable.scss';

const GameStatsTable = ({ gameStats, userId, onStatsUpdate }) => {
  // State now holds string values to allow for empty inputs during editing
  const [editingStats, setEditingStats] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Initialize editing stats, converting numbers from props to strings for the inputs
  useEffect(() => {
    const initialStats = {};
    gameStats.forEach(stat => {
      initialStats[stat.game_name] = {
        game_name: stat.game_name,
        total_games_played: String(stat.total_games_played || 0),
        total_daily_games_played: String(stat.total_daily_games_played || 0),
        best_daily_streak: String(stat.best_daily_streak || 0)
      };
    });
    setEditingStats(initialStats);
    setHasChanges(false);
  }, [gameStats]);

  // Check for changes by comparing the current string state to the original props (converted to string)
  useEffect(() => {
    const hasAnyChanges = gameStats.some(stat => {
      const edited = editingStats[stat.game_name];
      if (!edited) return false;
      
      return (
        edited.total_games_played !== String(stat.total_games_played || 0) ||
        edited.total_daily_games_played !== String(stat.total_daily_games_played || 0) ||
        edited.best_daily_streak !== String(stat.best_daily_streak || 0)
      );
    });
    
    setHasChanges(hasAnyChanges);
  }, [editingStats, gameStats]);

  // Handle input changes, allowing only numbers or an empty string
  const handleInputChange = (gameName, field, value) => {
    // Allow only numeric digits or an empty field
    if (/^[0-9]*$/.test(value)) {
      setEditingStats(prev => ({
        ...prev,
        [gameName]: {
          ...prev[gameName],
          [field]: value
        }
      }));
    }
    setError(null);
    setSuccess(false);
  };

  // NEW: Handle blur event to default empty inputs to '0'
  const handleInputBlur = (gameName, field, value) => {
    if (value.trim() === '') {
      setEditingStats(prev => ({
        ...prev,
        [gameName]: {
          ...prev[gameName],
          [field]: '0'
        }
      }));
    }
  };

  const validateAndPrepareStatsForSave = (stats) => {
    // Convert all values back to numbers for validation and saving
    const numericStats = {
      game_name: stats.game_name,
      total_games_played: parseInt(stats.total_games_played, 10) || 0,
      total_daily_games_played: parseInt(stats.total_daily_games_played, 10) || 0,
      best_daily_streak: parseInt(stats.best_daily_streak, 10) || 0
    };

    return numericStats;
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Prepare stats array, converting strings back to numbers and validating
      const statsArray = Object.values(editingStats).map(validateAndPrepareStatsForSave);
      
      const result = await onStatsUpdate(userId, statsArray);
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to update statistics');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const resetStats = {};
    gameStats.forEach(stat => {
      resetStats[stat.game_name] = {
        game_name: stat.game_name,
        total_games_played: String(stat.total_games_played || 0),
        total_daily_games_played: String(stat.total_daily_games_played || 0),
        best_daily_streak: String(stat.best_daily_streak || 0)
      };
    });
    setEditingStats(resetStats);
    setError(null);
    setSuccess(false);
  };

  const formatGameName = (gameName) => {
    const gameNameMap = {
      'slay-the-spire': 'Slay the Spire',
      'bible-books': 'Bible Books',
      'subnautica-creatures': 'Subnautica Creatures',
      'aoe2-units': 'Age of Empires II',
      'pokemon': 'Pokemon'
    };
    return gameNameMap[gameName] || gameName;
  };

  // No validation needed - current streaks are calculated on-demand

  if (gameStats.length === 0) {
    return (
      <div className="game-stats-table__empty">
        <div className="game-stats-table__empty-message">No game statistics found for this user.</div>
        <div className="game-stats-table__empty-subtitle">Statistics will appear here once the user starts playing games.</div>
      </div>
    );
  }

  return (
    <div className="game-stats-table">
      <div className="game-stats-table__controls">
        <div className="game-stats-table__info">Edit the values below and click Save to update the user's game statistics.</div>
        <div className="game-stats-table__actions">
          <button onClick={handleReset} disabled={!hasChanges || loading} className="game-stats-table__button game-stats-table__button--reset">Reset</button>
          <button onClick={handleSave} disabled={!hasChanges || loading} className="game-stats-table__button game-stats-table__button--save">{loading ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      {error && <div className="game-stats-table__error">{error}</div>}
      {success && <div className="game-stats-table__success">✅ Game statistics updated successfully!</div>}

      <div className="game-stats-table__info-note">
        <div className="game-stats-table__note">
          <strong>Note:</strong> Current Streak is calculated in real-time from daily game states and cannot be edited directly. To modify a user's streak, edit their daily_game_states records in the database.
        </div>
      </div>

      <div className="game-stats-table__container">
        <table className="game-stats-table__table">
          <thead>
            <tr>
              <th className="game-stats-table__header">Game</th>
              <th className="game-stats-table__header">Total Games</th>
              <th className="game-stats-table__header">Daily Games</th>
              <th className="game-stats-table__header">Best Streak</th>
            </tr>
          </thead>
          <tbody>
            {gameStats.map((stat) => {
              const editing = editingStats[stat.game_name] || {};
              return (
                <tr key={stat.game_name} className="game-stats-table__row">
                  <td className="game-stats-table__cell game-stats-table__cell--game">{formatGameName(stat.game_name)}</td>
                  <td className="game-stats-table__cell">
                    <input
                      type="text" // Use text to allow empty string
                      value={editing.total_games_played ?? ''}
                      onChange={(e) => handleInputChange(stat.game_name, 'total_games_played', e.target.value)}
                      onBlur={(e) => handleInputBlur(stat.game_name, 'total_games_played', e.target.value)}
                      className="game-stats-table__input"
                      disabled={loading}
                    />
                  </td>
                  <td className="game-stats-table__cell">
                    <input
                      type="text"
                      value={editing.total_daily_games_played ?? ''}
                      onChange={(e) => handleInputChange(stat.game_name, 'total_daily_games_played', e.target.value)}
                      onBlur={(e) => handleInputBlur(stat.game_name, 'total_daily_games_played', e.target.value)}
                      className="game-stats-table__input"
                      disabled={loading}
                    />
                  </td>
                  <td className="game-stats-table__cell">
                    <input
                      type="text"
                      value={editing.best_daily_streak ?? ''}
                      onChange={(e) => handleInputChange(stat.game_name, 'best_daily_streak', e.target.value)}
                      onBlur={(e) => handleInputBlur(stat.game_name, 'best_daily_streak', e.target.value)}
                      className="game-stats-table__input"
                      disabled={loading}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default GameStatsTable;